import type { Campaign, CampaignResendSegment } from '../../api/types';
import { defaultResendSegment, resendSegmentSize, resendSegmentsFor } from './campaignTools';

/**
 * What the Follow up panel offers on a finished campaign: every group the
 * server can resolve for this channel, sorted by what the operator is asking —
 * who engaged, who did not, who it never reached — each with its own count.
 *
 * Pure, so the grouping and the reasons a group is not on offer are tested
 * rather than eyeballed. The counts are the funnel's own (`resendSegmentSize`),
 * which `scripts/verify/campaignresend.js` holds to the server's predicates —
 * an operator picks "bounced · 26" and has to get 26.
 */

type SegmentKey = Exclude<CampaignResendSegment, 'custom'>;

export interface FollowUpGroup {
  key: SegmentKey;
  label: string;
  hint: string;
  count: number;
  /** Why it cannot be picked, when that is something other than "nobody is in it". */
  unavailable: string | null;
}

export interface FollowUpSection {
  id: 'engaged' | 'quiet' | 'unreached' | 'everyone';
  title: string;
  groups: FollowUpGroup[];
}

const SECTIONS: Record<'email' | 'sms', { id: FollowUpSection['id']; title: string; keys: SegmentKey[] }[]> = {
  email: [
    { id: 'engaged', title: 'Who showed interest', keys: ['clicked', 'unclicked', 'opened'] },
    { id: 'quiet', title: 'Who didn’t open it', keys: ['unopened'] },
    { id: 'unreached', title: 'Who it didn’t reach', keys: ['bounced', 'failed'] },
    { id: 'everyone', title: 'Everyone', keys: ['all'] },
  ],
  sms: [
    { id: 'engaged', title: 'Who it reached', keys: ['clicked', 'delivered'] },
    { id: 'quiet', title: 'Not confirmed', keys: ['undelivered'] },
    { id: 'unreached', title: 'Who it didn’t reach', keys: ['bounced', 'failed'] },
    { id: 'everyone', title: 'Everyone', keys: ['all'] },
  ],
};

/**
 * The panel's sections. A click group on a campaign that never counted clicks
 * is not "nobody clicked" — it is a question this campaign cannot answer — so it
 * stays listed with that said, rather than reading as a zero. `unclicked` goes
 * with it: without click counting every opener is "opened, did not click".
 */
export function followUpSections(
  campaign: Pick<Campaign, 'channel' | 'stats' | 'trackClicks'>,
): FollowUpSection[] {
  const channel = campaign.channel === 'sms' ? 'sms' : 'email';
  const choices = new Map(resendSegmentsFor(channel).map((choice) => [choice.key, choice]));
  const noClicks = campaign.trackClicks === false;
  return SECTIONS[channel].map((section) => ({
    id: section.id,
    title: section.title,
    groups: section.keys.flatMap((key) => {
      const choice = choices.get(key);
      if (!choice) return [];
      const clickGroup = key === 'clicked' || key === 'unclicked';
      return [
        {
          key,
          label: choice.label,
          hint: choice.hint,
          count: resendSegmentSize(campaign.stats, key),
          unavailable:
            clickGroup && noClicks
              ? channel === 'sms'
                ? 'Taps were not counted on this campaign'
                : 'Clicks were not counted on this campaign'
              : null,
        },
      ];
    }),
  }));
}

/** Whether a group can be picked: somebody is in it and it is a question this campaign answers. */
export const pickable = (group: FollowUpGroup) => group.count > 0 && group.unavailable === null;

/**
 * Where the panel opens. A next step that named a group opens on it when it can
 * be picked; otherwise the usual order — the people it missed before the people
 * it reached, because a follow-up is most often a second try.
 */
export function initialFollowUp(
  campaign: Pick<Campaign, 'channel' | 'stats' | 'trackClicks'>,
  pickedCount: number,
  asked?: CampaignResendSegment,
): CampaignResendSegment {
  const groups = followUpSections(campaign).flatMap((section) => section.groups);
  if (asked && asked !== 'custom') {
    const group = groups.find((one) => one.key === asked);
    if (group && pickable(group)) return asked;
  }
  return defaultResendSegment(campaign.stats, pickedCount, campaign.channel);
}

/**
 * The caution under a chosen group, when it carries one. Bounces are the one
 * group that can hurt the sender: an address that already came back is the
 * signal mail providers use to decide a sender is careless.
 */
export function followUpCaution(
  campaign: Pick<Campaign, 'channel' | 'stats'>,
  segment: CampaignResendSegment,
): string | null {
  const sms = campaign.channel === 'sms';
  const bounced = campaign.stats?.bounced ?? 0;
  if (segment === 'bounced') {
    return sms
      ? 'These numbers came back as a landline or a handset that does not exist. Texting them again fails the same way unless a number was wrong and has been fixed.'
      : 'These addresses came back undelivered. Mailing a dead address again hurts the sending domain — check them before you send.';
  }
  if (segment === 'all' && bounced > 0) {
    return `${bounced.toLocaleString()} of these bounced last time. Review & send offers to leave them out; nothing leaves them out on its own.`;
  }
  return null;
}
